import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, ExternalLink } from "lucide-react";
import { BOOKS } from "../../data/publicSite";
import usePageMeta from "../../lib/usePageMeta";
import BreadcrumbSchema from "../../components/public/BreadcrumbSchema";

export default function BookDetail() {
  const { slug } = useParams();
  const book = BOOKS.find((b) => b.slug === slug);
  usePageMeta({
    title: book ? `${book.title} | California Corporate Chaplains` : "Books & Resources | California Corporate Chaplains",
    description: book?.description || book?.subtitle,
  });
  if (!book) return <Navigate to="/books" replace />;

  return (
    <article className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-testid={`book-detail-${book.slug}`}>
      <BreadcrumbSchema
        id={`book-${book.slug}`}
        items={[
          { name: "Home", path: "/" },
          { name: "Books & Resources", path: "/books" },
          { name: book.title, path: `/books/${book.slug}` },
        ]}
      />
      <Link to="/books" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#4A6FA5] mb-8">
        <ArrowLeft className="w-4 h-4" /> Books & Resources
      </Link>

      <div className="grid lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-2">
          <div
            className="aspect-[3/4] rounded-2xl bg-white border border-slate-200 shadow-md flex items-center justify-center p-4 sm:p-5"
            data-testid={`book-detail-cover-${book.slug}`}
          >
            {book.coverImage ? (
              <img
                src={book.coverImage}
                alt={book.coverAlt || `Front cover of ${book.title} by ${book.author}.`}
                loading="eager"
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full rounded-xl bg-gradient-to-br from-[#4A6FA5] to-[#3B597B] flex flex-col items-center justify-center text-white p-10">
                <BookOpen className="w-10 h-10 mb-6 opacity-80" strokeWidth={1.5} />
                <p className="text-[10px] uppercase tracking-[0.3em] opacity-70 mb-4 text-center">Featured book</p>
                <h1 className="font-heading text-2xl md:text-3xl font-medium text-center leading-tight">{book.title}</h1>
                <p className="mt-4 text-sm opacity-90 text-center leading-relaxed italic">{book.subtitle}</p>
                <p className="mt-4 text-xs opacity-80 text-center">by {book.author}</p>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-3">
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-2">Book</p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">{book.title}</h2>
          <p className="mt-2 text-slate-600 text-lg italic">{book.subtitle}</p>
          <p className="mt-2 text-sm text-slate-500">by {book.author}</p>

          <div className="mt-6 rounded-xl border border-slate-200 bg-[#F8FAFC] p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Written for</p>
            <p className="text-sm text-slate-700 leading-relaxed">{book.audience}</p>
          </div>

          <p className="mt-6 text-base text-slate-700 leading-relaxed">{book.description}</p>

          <div className="mt-6 flex flex-wrap gap-2">
            {book.audiencesGrid.map((a) => (
              <span key={a} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-white border border-slate-200 text-slate-700">{a}</span>
            ))}
          </div>

          <div className="mt-10">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">Purchase & order</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={book.amazonUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-detail-amazon"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
              >
                Purchase on Amazon <ExternalLink className="w-4 h-4" />
              </a>
              <Link
                to={`/bulk-order?product=${book.slug === "from-diagnosis-to-goodbye" ? "from_diagnosis_to_goodbye" : "multiple"}`}
                data-testid="book-detail-bulk"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-700 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
              >
                Request a Bulk / Organizational Order
              </Link>
            </div>
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              Amazon links open in a new tab. Bulk and workshop inquiries are handled directly by California Corporate Chaplains.
            </p>
          </div>

          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5">
            <p className="text-sm text-slate-600 leading-relaxed">
              Looking to bring this material into a training or workshop for your team, hospice, healthcare organization, senior-care provider, or church community?
            </p>
            <Link
              to="/services/training-workshops"
              className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[#4A6FA5] hover:text-[#3B597B]"
            >
              Explore training & workshops <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {book.slug === "from-diagnosis-to-goodbye" && (
        <section
          className="mt-16 md:mt-20 rounded-2xl border border-slate-200 bg-gradient-to-br from-[#F0F4F8] via-white to-white p-8 md:p-10"
          data-testid="book-hospice-track-callout"
          aria-labelledby="book-hospice-track-heading"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold">
            CCCHAPS Chaplain Training Academy
          </p>
          <h2
            id="book-hospice-track-heading"
            className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight"
          >
            Want to Practice What You Read?
          </h2>
          <p className="mt-4 text-slate-700 leading-relaxed max-w-3xl">
            <em>From Diagnosis to Goodbye</em> is also the foundational text for the CCCHAPS Chaplain Training Academy's Hospice Chaplaincy Track.
          </p>
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-7 max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold">
              Track Three
            </p>
            <h3 className="mt-1 font-heading text-2xl text-slate-900 leading-tight">
              Hospice Chaplaincy
            </h3>
            <p className="mt-1 text-slate-600 italic">
              Practicing From Diagnosis to Goodbye
            </p>
            <p className="mt-4 text-sm text-slate-700 leading-relaxed">
              Designed for pastors, chaplains, and spiritual-care providers who want to translate the principles of the book into compassionate hospice chaplaincy practice.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3 items-center">
            <Link
              to="/academy/hospice-chaplaincy"
              data-testid="book-hospice-track-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] shadow-sm"
            >
              Explore the Hospice Chaplaincy Track <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/academy/portal/hosp-001"
              data-testid="book-hosp001-secondary-cta"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4A6FA5] hover:text-[#3B597B]"
            >
              Start with the Free HOSP-001 Preview <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}
    </article>
  );
}
